import { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  GraduationCap,
  Microscope,
  Briefcase,
  Languages,
  CheckCircle2,
} from "lucide-react";
import { SectionTitle } from "@/components/site/Features";

export default function Courses() {
  const [stream, setStream] = useState("science");

  return (
    <section
      id="courses"
      data-testid="courses-section"
      className="bg-white py-16 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="Courses Offered"
          title="From foundation to focused board & entrance prep."
          subtitle="Choose a stage to see exactly what we cover."
        />

        <div className="mt-10">
          <Tabs defaultValue="5-10" className="w-full">
            <TabsList
              data-testid="courses-tabs"
              className="grid h-auto w-full grid-cols-2 gap-2 rounded-md bg-slate-100 p-1.5 md:max-w-md"
            >
              <TabsTrigger
                value="5-10"
                data-testid="tab-5-10"
                className="rounded-md py-2.5 text-sm font-semibold data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                Classes 5 – 10
              </TabsTrigger>
              <TabsTrigger
                value="11-12"
                data-testid="tab-11-12"
                className="rounded-md py-2.5 text-sm font-semibold data-[state=active]:bg-red-600 data-[state=active]:text-white"
              >
                Classes 11 – 12
              </TabsTrigger>
            </TabsList>

            {/* Classes 5-10 */}
            <TabsContent value="5-10" className="mt-8 focus-visible:outline-none">
              <div className="grid gap-5 md:grid-cols-3">
                <MediumCard
                  testid="medium-marathi"
                  icon={GraduationCap}
                  title="Marathi Medium"
                  desc="Conceptual teaching aligned with the Maharashtra State Board syllabus."
                />
                <MediumCard
                  testid="medium-semi"
                  icon={Languages}
                  title="Semi-English Medium"
                  desc="Bridge medium with English for Maths & Science, Marathi for the rest."
                />
                <MediumCard
                  testid="medium-english"
                  icon={GraduationCap}
                  title="English Medium"
                  desc="Full English-medium coaching for SSC and CBSE-pattern board readiness."
                />
              </div>

              <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-5 md:p-6">
                <h4 className="font-display text-base font-bold text-slate-900 md:text-lg">
                  What every batch (Std 5 – 10) gets
                </h4>
                <ul className="mt-3 grid gap-2 text-[14px] text-slate-700 md:grid-cols-2">
                  {[
                    "Daily classroom practice & worksheets",
                    "Printed topic-wise notes",
                    "Weekly tests + monthly review",
                    "Doubt-solving with subject teachers",
                    "Parent-teacher progress meetings",
                    "Foundation modules (Std 9–10) for JEE/NEET aspirants",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>

            {/* Classes 11-12 */}
            <TabsContent
              value="11-12"
              className="mt-8 focus-visible:outline-none"
            >
              {/* Stream switch */}
              <div
                data-testid="stream-switch"
                className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm"
              >
                <StreamButton
                  active={stream === "science"}
                  onClick={() => setStream("science")}
                  icon={Microscope}
                  label="Science"
                  testid="stream-science-btn"
                />
                <StreamButton
                  active={stream === "commerce"}
                  onClick={() => setStream("commerce")}
                  icon={Briefcase}
                  label="Commerce"
                  testid="stream-commerce-btn"
                />
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-12">
                {/* Left description */}
                <div className="lg:col-span-5">
                  {stream === "science" ? (
                    <StreamPanel
                      testid="science-panel"
                      title="Science Stream"
                      desc="Designed for students aiming at engineering, medicine and pure sciences. The teaching covers the HSC board syllabus thoroughly while preparing simultaneously for JEE, NEET and MHT-CET."
                      groupTitle="Subject Combinations"
                      groups={[
                        { name: "PCMB", note: "Physics, Chemistry, Maths & Biology" },
                        { name: "PCM + CS / IT", note: "With Computer Science or Information Technology" },
                      ]}
                    />
                  ) : (
                    <StreamPanel
                      testid="commerce-panel"
                      title="Commerce Stream"
                      desc="A focused programme for HSC Commerce students aiming for CA, CS, CMA, BBA, BMS and B.Com. Strong emphasis on conceptual clarity in BK & OCM along with disciplined writing practice."
                      groupTitle="Subjects Covered"
                      groups={[
                        { name: "OCM", note: "Organisation of Commerce & Management" },
                        { name: "BK", note: "Book-Keeping & Accountancy" },
                        { name: "IT", note: "Information Technology" },
                        { name: "SP", note: "Secretarial Practice" },
                        { name: "Languages", note: "English, Marathi, Hindi" },
                      ]}
                    />
                  )}
                </div>

                {/* Right details */}
                <div className="lg:col-span-7">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-5 md:p-6">
                    <h4 className="font-display text-base font-bold text-slate-900 md:text-lg">
                      {stream === "science"
                        ? "Integrated Coaching: JEE / NEET / MHT-CET"
                        : "What our Commerce students get"}
                    </h4>
                    <ul className="mt-3 grid gap-2 text-[14px] text-slate-700 md:grid-cols-2">
                      {(stream === "science"
                        ? [
                            "JEE — concept building + advanced problem sets",
                            "NEET — NCERT mastery and Biology depth",
                            "MHT-CET — speed practice & full-length mocks",
                            "Board pattern aligned for HSC scoring",
                            "Separate doubt-clearing windows weekly",
                            "Performance tracking across all 3 fronts",
                          ]
                        : [
                            "Concept-first BK & OCM teaching",
                            "Daily journal & ledger practice",
                            "Detailed printed notes & summary sheets",
                            "Weekly objective + descriptive tests",
                            "Writing & presentation coaching for HSC",
                            "Career guidance for CA / CS / BBA / B.Com",
                          ]
                      ).map((t, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}

function MediumCard({ testid, icon: Icon, title, desc }) {
  return (
    <div
      data-testid={testid}
      className="rounded-md border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-red-200 hover:shadow-sm md:p-6"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-red-50 text-red-600">
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="mt-4 font-display text-base font-bold text-slate-900 md:text-lg">
        {title}
      </h4>
      <p className="mt-1.5 text-[14px] leading-relaxed text-slate-600">
        {desc}
      </p>
    </div>
  );
}

function StreamButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
        active
          ? "bg-red-600 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function StreamPanel({ testid, title, desc, groupTitle, groups }) {
  return (
    <div data-testid={testid}>
      <h3 className="font-display text-xl font-bold text-slate-900 md:text-2xl">
        {title}
      </h3>
      <p className="mt-2 text-[14px] leading-relaxed text-slate-600 md:text-[15px]">
        {desc}
      </p>
      <h4 className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-red-700">
        {groupTitle}
      </h4>
      <ul className="mt-3 space-y-2.5">
        {groups.map((g, i) => (
          <li
            key={i}
            className="flex items-baseline gap-3 rounded-md border border-slate-200 bg-white px-4 py-3"
          >
            <span className="font-display text-sm font-bold text-slate-900">
              {g.name}
            </span>
            <span className="text-[13px] text-slate-500">— {g.note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
